import type {
  EmployeeMetadata,
  FunnelStatus,
  TCTemplateRecord,
} from 'src/api/generated';
import { TCTemplateKind } from 'src/api/generated';
import type { MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import type {
  GuidedHandoffTemplateKind,
  GuidedHandoffTemplateValidation,
  GuidedHandoffValues,
} from 'src/modules/automations/types/quick-automation.type';
import { isTemplateDefinition } from './template-definition.util';
import {
  areRequiredFieldsMapped,
  buildDefaultMapping,
  getTemplateSchemaMetadata,
} from './template-mapping.util';

/**
 * Stable template keys that receive the guided handoff configuration.
 */
export const GUIDED_HANDOFF_TEMPLATE_KEYS = {
  Standard: 'handoff-on-human-message',
  ExceptContent: 'handoff-on-human-message-except-content',
} as const;

const HANDOFF_TRIGGER_ID = 'llamadapro.trigger.message_created';

const COMMON_INPUT_TYPES = {
  funnelConversationId: 'number',
  messageDirection: 'string',
  funnelId: 'number',
  employeeUserId: 'string',
  enabledStatuses: 'json',
  funnelStatusId: 'number',
  messageUserId: 'string',
} as const;

const REQUIRED_EVENT_MAPPINGS = {
  funnelConversationId: 'data.funnelConversationId',
  messageDirection: 'data.direction',
  messageUserId: 'data.userId',
} as const;

const resolveTemplateKind = (
  templateKey: string | undefined
): GuidedHandoffTemplateKind | null => {
  if (templateKey === GUIDED_HANDOFF_TEMPLATE_KEYS.Standard) {
    return 'standard';
  }
  if (templateKey === GUIDED_HANDOFF_TEMPLATE_KEYS.ExceptContent) {
    return 'except-content';
  }
  return null;
};

/**
 * Validates the complete frontend contract needed by the guided handoff form.
 *
 * Known templates fail closed when their trigger, input types, or locked event
 * mappings drift. Unknown templates remain eligible for the generic editor.
 *
 * @param template - Full template detail returned by Templates App.
 * @returns The recognized kind and whether guided configuration is safe.
 */
export const validateGuidedHandoffTemplate = (
  template: TCTemplateRecord | null
): GuidedHandoffTemplateValidation => {
  const kind = resolveTemplateKind(template?.key);
  if (!kind || !template) {
    return { kind: null, valid: false };
  }

  if (
    template.kind !== TCTemplateKind.WORKFLOW ||
    template.trigger?.id !== HANDOFF_TRIGGER_ID ||
    !isTemplateDefinition(template.definition)
  ) {
    return { kind, valid: false };
  }

  const { fieldExpectedLiteralTypes, requiredFields } =
    getTemplateSchemaMetadata(template);
  const expectedInputTypes: Record<string, string> = {
    ...COMMON_INPUT_TYPES,
    ...(kind === 'except-content'
      ? {
          messageContent: 'string',
          excludedMessageContent: 'string',
        }
      : {}),
  };

  const hasExpectedInputs = Object.entries(expectedInputTypes).every(
    ([field, expectedType]) => fieldExpectedLiteralTypes[field] === expectedType
  );
  if (!hasExpectedInputs) {
    return { kind, valid: false };
  }

  const defaultMapping = buildDefaultMapping(template);
  const guidedMappingPlaceholders: Record<string, MappingValue> = {
    ...defaultMapping,
    funnelId: {
      kind: 'literal',
      literalType: 'number',
      value: '1',
    },
    employeeUserId: {
      kind: 'literal',
      literalType: 'string',
      value: '1',
    },
    enabledStatuses: {
      kind: 'literal',
      literalType: 'json',
      value: '[1]',
    },
    funnelStatusId: {
      kind: 'literal',
      literalType: 'number',
      value: '1',
    },
    ...(kind === 'except-content'
      ? {
          excludedMessageContent: {
            kind: 'literal' as const,
            literalType: 'string' as const,
            value: 'placeholder',
          },
        }
      : {}),
  };
  if (!areRequiredFieldsMapped(guidedMappingPlaceholders, requiredFields)) {
    return { kind, valid: false };
  }

  const requiredEventMappings: Record<string, string> = {
    ...REQUIRED_EVENT_MAPPINGS,
    ...(kind === 'except-content'
      ? {
          messageContent: 'data.content',
        }
      : {}),
  };
  const hasExpectedEventMappings = Object.entries(requiredEventMappings).every(
    ([field, expectedPath]) => {
      if (!Object.prototype.hasOwnProperty.call(defaultMapping, field)) {
        return false;
      }
      const mappingValue = defaultMapping[field];
      return (
        mappingValue.kind === 'event' && mappingValue.path === expectedPath
      );
    }
  );

  return { kind, valid: hasExpectedEventMappings };
};

const normalizeStatusName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const matchesPorCalificarStatus = (value: string): boolean => {
  const match = /^por calificar \((.*)\)$/.exec(value);
  return match !== null && match[1].trim().length > 0;
};

/**
 * Suggests the statuses in which the handoff workflow should consider an AI
 * Agent active.
 *
 * @param statuses - Complete ordered status list for the current funnel.
 * @param destinationStatusId - Human destination that must not trigger itself.
 * @returns Matching status identifiers ordered by funnel position.
 */
export const suggestEnabledHandoffStatusIds = (
  statuses: FunnelStatus[],
  destinationStatusId: number
): number[] =>
  statuses
    .filter((status) => {
      if (status.id === destinationStatusId) {
        return false;
      }

      const name = normalizeStatusName(status.name);
      return (
        /(^|[^a-z0-9])ia([^a-z0-9]|$)/.test(name) ||
        name.includes('inteligencia artificial') ||
        name.includes('asistente virtual') ||
        name === 'identificar tipo de cliente' ||
        matchesPorCalificarStatus(name)
      );
    })
    .sort((left, right) => left.position - right.position)
    .map((status) => status.id);

/**
 * Returns the configured excluded-content default from a template mapping.
 *
 * @param mapping - Decoded template input mapping.
 * @returns The template literal, or an empty string when it is not configured.
 */
export const getExcludedContentDefault = (
  mapping: Record<string, MappingValue>
): string => {
  if (
    !Object.prototype.hasOwnProperty.call(mapping, 'excludedMessageContent')
  ) {
    return '';
  }
  const excludedContent = mapping.excludedMessageContent;
  return excludedContent.kind === 'literal' ? excludedContent.value : '';
};

/**
 * Verifies tenant-specific guided values before workflow creation.
 *
 * @param values - Values currently selected in the guided form.
 * @param statuses - Complete status list for the selected funnel.
 * @param employees - AI Agents available in the current tenant.
 * @param kind - Guided handoff variant being configured.
 * @returns True when every literal can be produced safely.
 */
export const areGuidedHandoffValuesValid = (
  values: GuidedHandoffValues,
  statuses: FunnelStatus[],
  employees: EmployeeMetadata[],
  kind: GuidedHandoffTemplateKind
): boolean => {
  const destination = statuses.find(
    (status) =>
      status.id === values.destinationStatusId &&
      status.funnelId === values.funnelId
  );
  if (!destination || values.enabledStatusIds.length === 0) {
    return false;
  }

  const uniqueEnabledIds = new Set(values.enabledStatusIds);
  const enabledStatusesAreValid =
    uniqueEnabledIds.size === values.enabledStatusIds.length &&
    !uniqueEnabledIds.has(destination.id) &&
    values.enabledStatusIds.every((statusId) =>
      statuses.some(
        (status) =>
          status.id === statusId && status.funnelId === values.funnelId
      )
    );
  if (!enabledStatusesAreValid) {
    return false;
  }

  const selectedEmployee = employees.find(
    (employee) =>
      employee.employeeId === values.employeeId &&
      typeof employee.userId === 'number'
  );
  if (!selectedEmployee) {
    return false;
  }

  return (
    kind !== 'except-content' || values.excludedMessageContent.trim().length > 0
  );
};

/**
 * Merges guided tenant literals into the mappings supplied by Templates App.
 *
 * @param baseMapping - Decoded mappings from the canonical template.
 * @param values - Validated guided handoff values.
 * @param employees - AI Agents used to resolve employee ID to user ID.
 * @param kind - Guided handoff variant being configured.
 * @returns A complete mapping ready for the existing instruction encoder.
 */
export const buildGuidedHandoffMapping = (
  baseMapping: Record<string, MappingValue>,
  values: GuidedHandoffValues,
  employees: EmployeeMetadata[],
  kind: GuidedHandoffTemplateKind
): Record<string, MappingValue> => {
  const selectedEmployee = employees.find(
    (employee) => employee.employeeId === values.employeeId
  );
  const employeeUserId =
    typeof selectedEmployee?.userId === 'number'
      ? String(selectedEmployee.userId)
      : '';

  const guidedMapping: Record<string, MappingValue> = {
    ...baseMapping,
    funnelId: {
      kind: 'literal',
      literalType: 'number',
      value: String(values.funnelId),
    },
    employeeUserId: {
      kind: 'literal',
      literalType: 'string',
      value: employeeUserId,
    },
    enabledStatuses: {
      kind: 'literal',
      literalType: 'json',
      value: JSON.stringify(values.enabledStatusIds),
    },
    funnelStatusId: {
      kind: 'literal',
      literalType: 'number',
      value:
        values.destinationStatusId === null
          ? ''
          : String(values.destinationStatusId),
    },
  };

  if (kind === 'except-content') {
    guidedMapping.excludedMessageContent = {
      kind: 'literal',
      literalType: 'string',
      value: values.excludedMessageContent.trim(),
    };
  }

  return guidedMapping;
};
